import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function useAuth(isAdminRoute = false) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentSession, setCurrentSession] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(true);

  // Enhanced session validation that respects role-based access
  const validateSession = async (session: any) => {
    if (!session) return false;
    
    // Check for admin route access
    if (isAdminRoute) {
      try {
        // First check user metadata for role
        const role = session.user?.user_metadata?.role;
        if (role === 'admin') return true;
        
        // Legacy check using the database function
        const { data: isAdmin, error: adminError } = await supabase.rpc('is_admin');
        
        if (adminError) {
          console.error("Admin check failed:", adminError);
          return false;
        }
        
        // If admin, update user metadata to cache this information for future checks
        if (isAdmin) {
          // Don't await this - we don't want to block the UI
          supabase.auth.updateUser({
            data: { role: 'admin' }
          }).then(({ error }) => {
            if (error) console.error("Failed to update user metadata with role:", error);
          });
          
          return true;
        }
        
        return false;
      } catch (error) {
        console.error("Admin verification error:", error);
        return false;
      }
    }
    
    return true; // User session is valid for non-admin routes
  };

  useEffect(() => {
    let mounted = true;
    
    // Initial session setup and validation
    const setupAuth = async () => {
      setIsValidating(true);
      
      // Set up auth state listener FIRST
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (_event, session) => {
          // Only update state if component is still mounted
          if (!mounted) return;
          
          setCurrentSession(session);
          
          // For security, validate the session with each change
          if (session) {
            const isValid = await validateSession(session);
            if (!isValid && isAdminRoute) {
              // Don't sign out - just redirect if needed
              navigate("/admin/login");
            }
          }
          
          setIsValidating(false);
        }
      );
      
      // THEN check for existing session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (mounted) {
        setCurrentSession(session);
        
        if (session) {
          const isValid = await validateSession(session);
          if (!isValid && isAdminRoute) {
            navigate("/admin/login");
          }
        }
        
        setIsValidating(false);
      }
      
      return () => {
        subscription.unsubscribe();
      };
    };
    
    setupAuth();
    
    return () => {
      mounted = false;
    };
  }, [navigate, isAdminRoute]);

  const checkSession = async () => {
    setLoading(true);
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error("Session check error:", error);
        toast.error("Authentication error: " + error.message);
        navigate("/");
        throw error;
      }
      
      if (!session) {
        // Remove sensitive logging
        return null;
      }

      // Validate the session based on route requirements
      const isValid = await validateSession(session);
      if (!isValid && isAdminRoute) {
        console.error("User is not authorized for this route");
        navigate("/");
        throw new Error("Admin access required");
      }
      
      setLoading(false);
      return session;
    } catch (error) {
      console.error("Session check failed:", error);
      setLoading(false);
      navigate("/");
      throw error;
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      await supabase.auth.signOut();
      toast.success("Logged out successfully");
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error("Error logging out");
    } finally {
      setLoading(false);
    }
  };

  return {
    checkSession,
    signOut,
    loading,
    session: currentSession,
    isValidating // Expose this so components can show loading state
  };
}
